import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import AppLayout from "@/layouts/AppLayout"
import { Head, usePage } from "@inertiajs/react"
import { IconArrowLeft, IconEdit } from "@tabler/icons-react"

interface UserAtLevel {
  id: number
  name: string
  email: string
  role: string
  avatar_url: string | null
  current_salary_display: string | null
}

interface LevelDetail {
  id: number
  title: string
  level: number
  level_group: string
  level_group_emoji: string
  keyword: string
  hourly_rate: string
  pl_incremental: string | null
  monthly_6h: string | null
  irpf_6h_monthly: string | null
  monthly_8h: string | null
  irpf_8h_monthly: string | null
  active: boolean
  description: string | null
  can_edit: boolean
}

interface Props {
  [key: string]: unknown
  seniority_level: LevelDetail
  users_at_level: UserAtLevel[]
}

function DetailCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm font-medium">{children}</dd>
    </div>
  )
}

export default function SeniorityLevelShow() {
  const { seniority_level: level, users_at_level: users } = usePage<Props>().props

  return (
    <>
      <Head title={`${level.title} - Seniority Levels - Operations Center`} />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/admin/seniority_levels">Seniority Levels</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{level.title}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                <a href="/admin/seniority_levels">
                  <IconArrowLeft className="h-4 w-4" />
                </a>
              </Button>
              <div>
                <h1 className="text-lg font-semibold tracking-tight">{level.title}</h1>
                <p className="text-sm text-muted-foreground">Level {level.level}</p>
              </div>
            </div>
            {level.can_edit && (
              <Button variant="outline" size="sm" asChild>
                <a href={`/admin/seniority_levels/${level.id}/edit`}>
                  <IconEdit className="mr-2 h-4 w-4" />
                  Edit Level
                </a>
              </Button>
            )}
          </div>
        </div>

        {/* Level Details */}
        <div className="rounded-xl border bg-card">
          <div className="p-6">
            <h2 className="mb-4 font-semibold">Level Details</h2>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
              <DetailCell label="Title">{level.title}</DetailCell>
              <DetailCell label="Level Group">
                {level.level_group_emoji} {level.level_group}
              </DetailCell>
              <DetailCell label="Keyword">
                <Badge variant="secondary" className="font-bold">
                  {level.keyword}
                </Badge>
              </DetailCell>
              <DetailCell label="P/L Incremental">{level.pl_incremental || "TBD"}</DetailCell>
              <DetailCell label="Hourly Rate">{level.hourly_rate}/hr</DetailCell>
              <DetailCell label="Status">
                <Badge
                  variant={level.active ? "default" : "destructive"}
                  className={level.active ? "bg-green-600" : ""}
                >
                  {level.active ? "Active" : "Inactive"}
                </Badge>
              </DetailCell>
            </dl>

            <Separator className="my-6" />

            <h3 className="mb-4 text-sm font-semibold">Compensation Details</h3>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2 lg:grid-cols-4">
              <DetailCell label="6H Monthly">
                <span className="font-semibold tabular-nums">{level.monthly_6h || "TBD"}</span>
              </DetailCell>
              <DetailCell label="IRPF 6H Monthly">
                <span className="tabular-nums">{level.irpf_6h_monthly || "-"}</span>
              </DetailCell>
              <DetailCell label="8H Monthly">
                <span className="font-semibold tabular-nums">{level.monthly_8h || "TBD"}</span>
              </DetailCell>
              <DetailCell label="IRPF 8H Monthly">
                <span className="tabular-nums">{level.irpf_8h_monthly || "-"}</span>
              </DetailCell>
            </dl>

            {level.description && (
              <>
                <Separator className="my-6" />
                <DetailCell label="Description">{level.description}</DetailCell>
              </>
            )}
          </div>
        </div>

        {/* Users at this Level */}
        <div className="rounded-xl border bg-card">
          <div className="p-6 pb-0">
            <h2 className="font-semibold">
              Users at this Level
              <span className="ml-2 text-sm font-normal text-muted-foreground">({users.length})</span>
            </h2>
          </div>
          <div className="p-6">
            {users.length > 0 ? (
              <div className="divide-y">
                {users.map((user) => (
                  <div key={user.id} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                    <div className="flex items-center gap-3">
                      {user.avatar_url ? (
                        <img className="h-10 w-10 rounded-full" src={user.avatar_url} alt={user.name} />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                          <span className="text-sm font-medium">{user.name.charAt(0)}</span>
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium">{user.name}</p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                        <div className="mt-1 flex items-center gap-3">
                          <Badge variant="secondary" className="capitalize">
                            {user.role}
                          </Badge>
                          {user.current_salary_display && (
                            <span className="text-xs text-muted-foreground">Salary: {user.current_salary_display}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" asChild>
                      <a href={`/admin/users/${user.id}`}>View Profile</a>
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center">
                <p className="text-sm font-medium">No users assigned</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  No users are currently assigned to this seniority level.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

SeniorityLevelShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
